import { Notification } from "@arco-design/web-react";
import { TFunction } from "i18next";

export type ApiError = {
  response?: {
    status: number;
    data: string | { error?: string; message?: string };
  };
  message?: string;
};

const getResponseMessage = (data: ApiError["response"] extends infer R ? R extends { data: infer D } ? D : never : never): string => {
  if (typeof data === "string") {
    return data;
  }
  if (data && typeof data === "object") {
    if (typeof data.error === "string") {
      return data.error;
    }
    if (typeof data.message === "string") {
      return data.message;
    }
  }
  return "";
};

const getErrorKey = (data: string, status: number): string => {
  const errorMap: Record<string, string> = {
    "missing heading or content": "missing_fields",
    "max upload count exceeded": "max_upload_exceeded",
    "invalid document type": "unsupported_document_type",
    "document size exceeded": "document_size_exceeded",
    "failed to get multipart form": "multipart_form_error",
    "failed to process document": "document_upload_failed",
    "failed to upload document": "document_upload_failed",
    "failed to create post": "post_create_failed",
    "failed to save post": "post_create_failed",
  };

  return errorMap[data] || (status >= 500 ? "server_error" : "default");
};

export const handleFeedApiError = (error: unknown, t: TFunction): void => {
  const defaultError = t("form_validation_messages.default");

  if (!error || typeof error !== "object") {
    Notification.error({ content: defaultError });
    return;
  }

  const apiError = error as ApiError;

  if (!apiError.response) {
    Notification.error({ content: defaultError });
    return;
  }

  const { status } = apiError.response;
  const message = getResponseMessage(apiError.response.data);
  const errorKey = getErrorKey(message, status);

  if (errorKey === "document_size_exceeded") {
    Notification.error({
      content: t("form_validation_messages.document_size_exceeded", { size: 25 }),
      duration: 5000,
    });
    return;
  }

  Notification.error({
    content: t(`form_validation_messages.${errorKey}`),
    duration: 5000,
  });
};
